import type { ReactNode } from 'react';

import type { TpMessageKeys } from 'src/features/intl';

export const QuizStepType = {
  OTHER: 'other',
  TEXT_CARD: 'textCard',
  SINGLE_OPTION: 'singleOption',
  SINGLE_OPTION_MINIMAL: 'singleOptionMinimal',
  SINGLE_OPTION_WITH_IMAGE_SELECT: 'singleOptionWithImageSelect',
  MULTIPLE_OPTIONS_SELECT: 'multipleOptionsSelect',
  MULTIPLE_OPTIONS_WITH_IMAGE_SELECT: 'multipleOptionsWithImageSelect',
  REVIEWS: 'reviews',
  ACHIEVE_RESULTS: 'achieveResults',
} as const;

export type QuizStepTypeValue =
  (typeof QuizStepType)[keyof typeof QuizStepType];

export type QuizSection =
  | 'Goals'
  | 'Lifestyle'
  | 'Nutrition'
  | 'Your Skin'
  | 'Personalisation'
  | 'Feelings'
  | 'Final Screens'
  | 'SingleSection'
  | 'Check up'
  | 'Level of stress'
  | 'Medical indications'
  | 'Wellbeing'
  | 'Facial care'
  | 'Final steps'
  | 'Goal'
  | 'Health Status'
  | 'Breathing'
  | 'Schedule'
  | 'Signs and predictors'
  | 'Self-assessment'
  | 'Motivation';

export enum QuizStep {
  CHALLENGE_PEOPLE_JOIN,
  CHALLENGE_BEAUTY_ROUTINE,
  CHALLENGE_SKIN_TYPE_ADOPTED,
  CHALLENGE_CHECKOUT,
  CHALLENGE_LOADING,
  WELCOME_LOADER,
  WELCOME_LOADER_SECOND,
  EASILY_SPEND,
  SATISFIED, // ?? in use ??
  WORRIES_MOST,
  FOCUS_ON,
  FOCUS_ON_TWO,
  AGE_CHANGES,
  WRINKLES,
  CHANGES_FACED,
  PRACTICE_CARE,
  SUIT_SCHEDULE_MORNING,
  SUIT_SCHEDULE_EVENING,
  BAD_HABITS,
  EAT_HABITS,
  SUGAR_HABITS,
  SODIUM_HABITS,
  VEGETABLES_HABITS,
  WATER_HABITS,
  SWELL_HABITS,
  SLEEP_HABITS,
  SKIN_TYPE,
  OILY_SHINE,
  AFTER_CLEAN_FACE,
  SKIN_ACNE,
  SKIN_TEXTURE,
  SKIN_COLOR,
  SKIN_PROBLEMS_WHITE_SIMPLE,
  SKIN_PROBLEMS_BLACK_SIMPLE,
  STRESS,
  DESIRED_RESULTS,
  SPEND_ON_CARE,
  CHOOSE_SUITABLE_PRODUCTS,
  SKIN_RECOMMEND,
  FIRST_CHANGES,
  AGE,
  FACE_SHAPE,
  JAWLINE,
  UNHAPPY,
  PLASTIC_SURGERY,
  DARK_CIRCLES,
  SKIN_SUMMARY,
  LIFESTYLE,
  WORK,
  AFRAID_LOSE_BEAUTY,
  AFRAID_LOSE_BEAUTY_BREAKS_TEST,
  APPEARANCE_REASON,
  APPEARANCE_REASON_BREAKS_TEST,
  TRENDS,
  HEAR_ABOUT_US,
  ACHIEVE_RESULTS,
  ENTER_EMAIL,
  MILLION_USERS,
  CHANGES_WORRIES_MOST,
  MODEL,
  RADIAL_PROGRESS,
  GOALS_TIMELINE,
  INNER_AGE,
  SUIT_SCHEDULE_EVENING_MAP,
  SUIT_SCHEDULE_MORNING_MAP,
  NUTRITION_OPENER,
  NUTRITION_SUMMARY,
  AGE_SIMPLE,
  WE_STUDIED_SIMPLE,
  PERSONALISED_PROGRAMS_SIMPLE,
  DEVELOPED_SYSTEM_SIMPLE,
  EASILY_SPEND_SIMPLE,
  EXCESSIVE_WEIGHT_SIMPLE,
  FACIAL_CARE_SIMPLE,
  DESIRED_RESULTS_SIMPLE,
  MEDICATIONS_SIMPLE,
  PREGNANT_SIMPLE,
  BAD_HABITS_SIMPLE,
  SKIN_CARE_SIMPLE,
  KIDS_SIMPLE,
  WORK_FROM_HOME_SIMPLE,
  IMPROVE_SLEEP_SIMPLE,
  HEADACHES_SIMPLE,
  BACK_ACHES_SIMPLE,
  SPECIFIC_AREAS_SIMPLE,
  SPECIFIC_ACTIVITIES_SIMPLE,
  TENSION_SIMPLE,
  SITUATIONS_SIMPLE,
  BEHAVIOUR_SIMPLE,
  WITH_MIMIKA_SIMPLE,
  BEST_VERSION_SIMPLE,
  MAIN_MOTIVATION_SIMPLE,
  MENTAL_WELLNESS_SIMPLE,
  WANT_TO_EXPLORE_SIMPLE,
  ACTION_PLAN_SIMPLE,
  RADIAL_PROGRESS_SIMPLE,
  RADIAL_PROGRESS_TWO_SIMPLE,
  LESS_COSTLY_SIMPLE,
  SAVE_MONEY_SIMPLE,
  GOALS_TIMELINE_SIMPLE,
  JUST_ROUTINE_SIMPLE,
  SLEEP_KEY_SIMPLE,
  BEAUTY_STARTS_SIMPLE,
  IMPROVE_QUALITY_SIMPLE,
  GREAT_JOB_SIMPLE,
  ECO_FRIENDLY_SIMPLE,
  AMAZING_STORIES_SIMPLE,
  ENTER_EMAIL_SIMPLE,
  BEST_SKIN_SIMPLE,
  GOAL_PLAN_SIMPLE,
  DISCOMFORT_SIMPLE,
  NAME,
  SKIN_CARE_GOALS_SIMPLE,
  SKIN_ALLERGIES_SIMPLE,
  ONE_WEEK,
  RID_OF,
  ELIMINATION,
  BOOST_RESULT,
  UNIQUE_TECHNOLOGIES,
  LONG_PREPAYWALL,
  AGE_CHANGES_STRESSLESS,
  TIREDNESS_STRESSLESS,
  CHANGES_WORRIES_MOST_STRESSLESS,
  EASILY_SPEND_STRESSLESS,
  SUIT_SCHEDULE_EVENING_STRESSLESS,
  SUIT_SCHEDULE_MORNING_STRESSLESS,
  BAD_HABITS_STRESSLESS,
  DARK_CIRCLES_STRESSLESS,
  SWELL_HABITS_STRESSLESS,
  ACNE_STRESSLESS,
  SKIN_TEXTURE_STRESSLESS,
  OILY_SHINE_STRESSLESS,
  TIP_ONE_STRESSLESS,
  TIP_TWO_STRESSLESS,
  BEST_DAY_STRESSLESS,
  PERSONALIZED_STRESSLESS,
  WITHOUT_MIMIKA_HEALTH,
  PERSONAL_PLAN_HEALTH,
  JUST_ROUTINE_HEALTH,
  PERSONALISED_PROGRAMS_HEALTH,
  WE_STUDIED_HEALTH,
  DESIRE_RESULTS_HEALTH,
  STRESS_HEALTH,
  NUTRITION_SUMMARY_HEALTH,
  WELCOME_LOADER_SECOND_HEALTH,
  AMAZING_STORIES_HEALTH,
}

type BaseQuizStep = {
  type: QuizStepTypeValue;
  gaEventName?: string;
  section: QuizSection;
  step: QuizStep;
  nextButtonText?: string | ReactNode;
  nextButtonId?: TpMessageKeys;
  skipButtonText?: string | ReactNode;
  title?: ReactNode;
  titleId?: TpMessageKeys;
  markId?: TpMessageKeys;
  markDefaultMessage?: string;
  defaultTitleMessage?: string;
  subtitle?: ReactNode;
  subtitleId?: TpMessageKeys;
  defaultSubtitleMessage?: string;
  TitleComponent?: ReactNode;
  SubTitleComponent?: ReactNode;
  imagesForPreload?: Array<string>;
  image?: ReactNode;
  tooltip?: ReactNode;
};

export type OtherQuizStep = BaseQuizStep & {
  type: 'other';
  options?: SingleOptionSelectQuizStepOption[];
  background?: ColoredBackgroundQuizStepBackground;
  image?: ReactNode | string;
  imageHeight?: number;
  tooltip?: ReactNode;
};

export interface TextCardQuizStep extends BaseQuizStep {
  type: 'textCard';
  image?: ReactNode;
  background: ColoredBackgroundQuizStepBackground;
}

export interface SelectQuizStepOption<T> {
  value: T;
  label?: string | ReactNode;
  labelId?: TpMessageKeys;
  labelDefaultMessage?: string;
}

export interface SingleOptionSelectQuizStepOption
  extends SelectQuizStepOption<number> {
  image?: ReactNode;
  imageColor?: string;
}

export type TpReviewsQuizStepReview = {
  image: ReactNode;
  title: {
    id: TpMessageKeys;
    defaultMessage: string;
  };
  description: {
    id: TpMessageKeys;
    defaultMessage: string;
  };
};
export type TpReviewsQuizStep = BaseQuizStep & {
  type: 'reviews';
};

export type TpAchieveResultsQuizStep = BaseQuizStep & {
  type: 'achieveResults';
};

export type SingleOptionSelectQuizStep = BaseQuizStep & {
  type: 'singleOption';
  options: SingleOptionSelectQuizStepOption[];
  background?: ColoredBackgroundQuizStepBackground;
  image?: ReactNode;
};

export type SingleOptionImageSelectQuizStep = BaseQuizStep & {
  type: 'singleOptionWithImageSelect';
  options: SingleOptionSelectQuizStepOption[];
};

export type MultipleOptionsSelectQuizStepOption =
  SelectQuizStepOption<string> & {
    image?: ReactNode;
  };

export interface MultipleOptionsSelectQuizStep extends BaseQuizStep {
  type: 'multipleOptionsSelect';
  options: MultipleOptionsSelectQuizStepOption[];
  skipButtonText?: string | ReactNode;
  skipOptionText?: MultipleOptionsSelectQuizStepOption;
  extraContent?: ReactNode;
}

export type MultipleOptionsImageSelectQuizStep = BaseQuizStep & {
  type: 'multipleOptionsWithImageSelect';
  options: MultipleOptionsSelectQuizStepOption[];
  skipButtonText?: string | ReactNode;
  skipOptionText?: MultipleOptionsSelectQuizStepOption;
};

export enum ColoredBackgroundQuizStepBackground {
  WHITE,
  BROWN,
  BROWN_LIGHT,
  CREAM,
  BLUE,
  PASTEL_GREEN,
  GREEN,
  LIGHT_PINK,
  PINK,
  PINK_VARIANT,
  DEFAULT,
  YELLOW,
  GREY,
  LIGHT_GREY,
  SOFT_GREEN,
  LIGHT_BEIGE,
  LIGHT_BLUE,
  PASTEL,
  SECONDARY_GREEN,
  SECONDARY_PINK,
  SECONDARY_ORANGE,
  LINEAR_ORANGE,
}

export type TpStep =
  | OtherQuizStep
  | TextCardQuizStep
  | SingleOptionSelectQuizStep
  | SingleOptionImageSelectQuizStep
  | MultipleOptionsSelectQuizStep
  | MultipleOptionsImageSelectQuizStep
  | TpReviewsQuizStep
  | TpAchieveResultsQuizStep;

export const isStepHeaderShown = (step: TpStep) => {
  switch (step.type) {
    case QuizStepType.OTHER:
      switch (step.step) {
        case QuizStep.ENTER_EMAIL:
        case QuizStep.WELCOME_LOADER:
        case QuizStep.WELCOME_LOADER_SECOND:
        case QuizStep.WELCOME_LOADER_SECOND_HEALTH:
        case QuizStep.ENTER_EMAIL_SIMPLE:
        case QuizStep.MILLION_USERS:
        case QuizStep.WE_STUDIED_SIMPLE:
        case QuizStep.WE_STUDIED_HEALTH:
        case QuizStep.PERSONALISED_PROGRAMS_SIMPLE:
        case QuizStep.PERSONALISED_PROGRAMS_HEALTH:
        case QuizStep.DEVELOPED_SYSTEM_SIMPLE:
        case QuizStep.LESS_COSTLY_SIMPLE:
        case QuizStep.SAVE_MONEY_SIMPLE:
        case QuizStep.MODEL:
        case QuizStep.RADIAL_PROGRESS:
        case QuizStep.RADIAL_PROGRESS_SIMPLE:
        case QuizStep.RADIAL_PROGRESS_TWO_SIMPLE:
        case QuizStep.GOALS_TIMELINE:
        case QuizStep.GOALS_TIMELINE_SIMPLE:
        case QuizStep.SUIT_SCHEDULE_EVENING_MAP:
        case QuizStep.SUIT_SCHEDULE_MORNING_MAP:
        case QuizStep.NUTRITION_OPENER:
        case QuizStep.NUTRITION_SUMMARY:
        case QuizStep.NUTRITION_SUMMARY_HEALTH:
        case QuizStep.NAME:
        case QuizStep.JUST_ROUTINE_SIMPLE:
        case QuizStep.JUST_ROUTINE_HEALTH:
        case QuizStep.CHOOSE_SUITABLE_PRODUCTS:
        case QuizStep.SLEEP_KEY_SIMPLE:
        case QuizStep.BEAUTY_STARTS_SIMPLE:
        case QuizStep.IMPROVE_QUALITY_SIMPLE:
        case QuizStep.GREAT_JOB_SIMPLE:
        case QuizStep.ECO_FRIENDLY_SIMPLE:
        case QuizStep.AMAZING_STORIES_SIMPLE:
        case QuizStep.AMAZING_STORIES_HEALTH:
        case QuizStep.BEST_SKIN_SIMPLE:
        case QuizStep.GOAL_PLAN_SIMPLE:
        case QuizStep.PERSONALIZED_STRESSLESS:
        case QuizStep.WITHOUT_MIMIKA_HEALTH:
        case QuizStep.PERSONAL_PLAN_HEALTH:
        case QuizStep.DESIRE_RESULTS_HEALTH:
        case QuizStep.CHALLENGE_PEOPLE_JOIN:
        case QuizStep.CHALLENGE_SKIN_TYPE_ADOPTED:
        case QuizStep.CHALLENGE_BEAUTY_ROUTINE:
        case QuizStep.CHALLENGE_CHECKOUT:
        case QuizStep.CHALLENGE_LOADING:
          return false;

        default:
          return true;
      }

    default:
      return true;
  }
};

export const isStepProgressShown = (step: TpStep) => {
  switch (step.type) {
    case QuizStepType.OTHER:
      switch (step.step) {
        case QuizStep.WELCOME_LOADER:
        case QuizStep.WELCOME_LOADER_SECOND:
        case QuizStep.WELCOME_LOADER_SECOND_HEALTH:
        case QuizStep.SKIN_RECOMMEND:
        case QuizStep.CHOOSE_SUITABLE_PRODUCTS:
        case QuizStep.FIRST_CHANGES:
        case QuizStep.MILLION_USERS:
        case QuizStep.MODEL:
        case QuizStep.RADIAL_PROGRESS:
        case QuizStep.GOALS_TIMELINE:
        case QuizStep.GOALS_TIMELINE_SIMPLE:
        case QuizStep.SUIT_SCHEDULE_EVENING_MAP:
        case QuizStep.SUIT_SCHEDULE_MORNING_MAP:
        case QuizStep.NUTRITION_OPENER:
        case QuizStep.NUTRITION_SUMMARY:
        case QuizStep.NUTRITION_SUMMARY_HEALTH:
        case QuizStep.JUST_ROUTINE_SIMPLE:
        case QuizStep.JUST_ROUTINE_HEALTH:
        case QuizStep.SLEEP_KEY_SIMPLE:
        case QuizStep.BEAUTY_STARTS_SIMPLE:
        case QuizStep.IMPROVE_QUALITY_SIMPLE:
        case QuizStep.GREAT_JOB_SIMPLE:
        case QuizStep.ECO_FRIENDLY_SIMPLE:
        case QuizStep.AMAZING_STORIES_SIMPLE:
        case QuizStep.AMAZING_STORIES_HEALTH:
        case QuizStep.BEST_SKIN_SIMPLE:
        case QuizStep.GOAL_PLAN_SIMPLE:
        case QuizStep.NAME:
        case QuizStep.WE_STUDIED_SIMPLE:
        case QuizStep.WE_STUDIED_HEALTH:
        case QuizStep.PERSONALISED_PROGRAMS_SIMPLE:
        case QuizStep.PERSONALISED_PROGRAMS_HEALTH:
        case QuizStep.LESS_COSTLY_SIMPLE:
        case QuizStep.DEVELOPED_SYSTEM_SIMPLE:
        case QuizStep.SAVE_MONEY_SIMPLE:
        case QuizStep.ENTER_EMAIL_SIMPLE:
        case QuizStep.RADIAL_PROGRESS_SIMPLE:
        case QuizStep.ONE_WEEK:
        case QuizStep.RID_OF:
        case QuizStep.ELIMINATION:
        case QuizStep.BOOST_RESULT:
        case QuizStep.UNIQUE_TECHNOLOGIES:
        case QuizStep.LONG_PREPAYWALL:
        case QuizStep.TIP_ONE_STRESSLESS:
        case QuizStep.TIP_TWO_STRESSLESS:
        case QuizStep.BEST_DAY_STRESSLESS:
        case QuizStep.PERSONALIZED_STRESSLESS:
        case QuizStep.WITHOUT_MIMIKA_HEALTH:
        case QuizStep.PERSONAL_PLAN_HEALTH:
        case QuizStep.DESIRE_RESULTS_HEALTH:
        case QuizStep.CHALLENGE_PEOPLE_JOIN:
        case QuizStep.CHALLENGE_SKIN_TYPE_ADOPTED:
        case QuizStep.CHALLENGE_BEAUTY_ROUTINE:
        case QuizStep.CHALLENGE_CHECKOUT:
        case QuizStep.CHALLENGE_LOADING:
          return false;
        default:
          return true;
      }
    case QuizStepType.TEXT_CARD:
    case QuizStepType.ACHIEVE_RESULTS:
      return false;

    default:
      return true;
  }
};

export enum QuizAgeOption {
  FROM_18_TO_24 = 0,
  FROM_25_TO_34 = 1,
  FROM_35_TO_44 = 2,
  FROM_45_TO_54 = 3,
  FROM_55_TO_64 = 4,
  FROM_65 = 5,
}

export enum OptionVariant {
  DEFAULT,
  GREEN,
}

export type OnboardingHeaderProps = {
  onPreviousPress: () => void;
  onSkipPress: undefined | (() => void);
  showProgressBar?: boolean;
  showBurgerMenu: boolean;
  country?: string;
  calculateProgress: ({ quizSection }: { quizSection: QuizSection }) => number;
  sectionName: string;
  showPreviousButton?: boolean;
};
